/**
 * 수업 예약/수정 모달의 날짜·시간 기본값 및 조합 유틸 (KST 기준).
 *
 * 모달 UI는 날짜("YYYY-MM-DD") 1개 + 시작/종료 시간("HH:mm")으로 입력받고,
 * 제출 시 `composeDateTime`으로 백엔드 계약 형식("YYYY-MM-DDTHH:mm")을 만든다.
 */

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const TEN_MINUTES_MS = 10 * 60 * 1000;
/** 기본 수업 길이(분). */
const DEFAULT_DURATION_MINUTES = 50;
/** 같은 날짜 모델에서 표현 가능한 마지막 시각(분). 23:50. */
const LAST_TIME_OF_DAY_MINUTES = 23 * 60 + 50;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** "HH:mm" → 분(0~1439). */
function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

/** 분 → "HH:mm". 같은 날짜 모델 보호를 위해 0~23:50 범위로 clamp한다. */
function minutesToTime(total: number): string {
  const clamped = Math.min(Math.max(total, 0), LAST_TIME_OF_DAY_MINUTES);
  return `${pad2(Math.floor(clamped / 60))}:${pad2(clamped % 60)}`;
}

/** Date를 KST 벽시계 datetime-local 문자열("YYYY-MM-DDTHH:mm")로 변환한다. */
function toKSTDatetimeLocal(date: Date): string {
  return new Date(date.getTime() + KST_OFFSET_MS).toISOString().substring(0, 16);
}

/**
 * 현재 시각 기준 기본 시작값. 분을 10분 단위로 올림한 KST 날짜·시간을 분리 반환.
 * 예: UTC 05:37:23 → KST 14:40 → `{ date: "2026-06-05", time: "14:40" }`
 */
export function defaultScheduleStartParts(now: Date): { date: string; time: string } {
  const ceiled = new Date(Math.ceil(now.getTime() / TEN_MINUTES_MS) * TEN_MINUTES_MS);
  const local = toKSTDatetimeLocal(ceiled); // "YYYY-MM-DDTHH:mm"
  const [date, time] = local.split("T");
  return { date, time };
}

/**
 * 기본 종료 시간 = 시작 시간 + 기본 수업 길이(50분).
 * 같은 날짜 모델을 보호하기 위해 자정을 넘기면 23:50으로 clamp한다.
 */
export function defaultEndTime(startTime: string): string {
  return minutesToTime(timeToMinutes(startTime) + DEFAULT_DURATION_MINUTES);
}

/**
 * 시작 시간을 옮길 때 기존 수업 길이(prevEnd - prevStart)를 유지하는 새 종료 시간.
 * 수정 모달에서 시작만 바꿔도 수업 길이가 보존되도록 쓴다(0~23:50으로 clamp).
 */
export function shiftEndPreservingDuration(
  prevStart: string,
  prevEnd: string,
  nextStart: string,
): string {
  const duration = Math.max(timeToMinutes(prevEnd) - timeToMinutes(prevStart), 0);
  return minutesToTime(timeToMinutes(nextStart) + duration);
}

/** 날짜("YYYY-MM-DD")와 시간("HH:mm")을 백엔드 계약 형식("YYYY-MM-DDTHH:mm")으로 합친다. */
export function composeDateTime(date: string, time: string): string {
  return `${date}T${time}`;
}
